import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';

// Curation of the client ordering portal's catalogue. Pricing is live:
// publishing records WHICH supplier the price is taken from, and the client
// price is computed on every request from that supplier's current cost and the
// margin (product override, else client override, else the default). Nothing
// about the price is stored, so a distributor price change reaches the portal
// on its own. Costs and margins on this screen are internal only and never
// reach the portal.

const pounds = (pence) => pence == null ? '—' : `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sell = (costPence, marginPct) => Math.round((costPence * (100 + marginPct)) / 100);

export default function PortalCataloguePage() {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [defaultMargin, setDefaultMargin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState('catalogue');

  const load = useCallback(() => {
    Promise.all([
      api.get('portal-products').catch(() => ({ products: [] })),
      api.get('portal-products/client-margins').catch(() => ({ clients: [] })),
      api.get('app-settings/portal_default_margin_pct').catch(() => null),
    ]).then(([p, c, s]) => {
      setProducts(p.products || []);
      setClients(c.clients || []);
      setDefaultMargin(s?.value != null && s.value !== '' ? Number(s.value) : null);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const run = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); return true; }
    catch (e) { toast.error(e.data?.error || e.message || 'That did not work.'); return false; }
  };

  const findCandidates = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try { setCandidates((await api.get('portal-products/candidates', { search })).candidates); }
    catch (e) { toast.error(e.data?.error || 'Search failed.'); }
    finally { setSearching(false); }
  };


  return (
    <div className="fade-in">
      <div className="mb-3 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Portal Catalogue</h2>
          <div className="text-[9px] text-gray-500">
            What clients can order, and at what margin. Prices are live: the client price is the current supplier cost plus the margin, so a distributor price change reaches the portal on its own.
          </div>
        </div>
        <div className="text-[9px] text-gray-500 bg-white border border-gray-200 rounded px-3 py-1.5">
          Default margin: <span className="font-bold text-navy">{defaultMargin != null ? `${defaultMargin}%` : 'not set'}</span>
          <span className="text-gray-400"> (markup on cost; an admin changes it in Settings)</span>
        </div>
      </div>

      <div className="flex gap-1.5 mb-3">
        {[['catalogue', 'Published products'], ['publish', 'Publish a product'], ['margins', 'Client margins']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`border rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-wider ${tab === id ? 'bg-navy border-navy text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'catalogue' && (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[8px] uppercase text-gray-500 font-bold">
            <div className="flex-1">Product</div>
            <div className="w-28">Priced from</div>
            <div className="w-24 text-right">Cost now</div>
            <div className="w-20 text-right">Margin</div>
            <div className="w-24 text-right">Client price*</div>
            <div className="w-28 text-right">Actions</div>
          </div>
          {products.map(p => {
            const margin = p.margin_pct_override ?? defaultMargin;
            return (
              <div key={p.id} className={`flex items-center gap-3 px-3 py-2 border-b border-gray-50 last:border-0 ${p.status === 'hidden' ? 'opacity-50' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-navy truncate">{p.display_name}</div>
                  <div className="text-[8px] text-gray-400">{p.sku} · {p.category}{p.status === 'hidden' ? ' · HIDDEN' : ''}</div>
                </div>
                <div className="w-28 text-[9px] text-gray-500">
                  {p.chosen_supplier_name || <span className="font-bold text-amber-700">no supplier</span>}
                </div>
                <div className="w-24 text-right text-[10px] text-gray-600">{pounds(p.current_cost_pence)}</div>
                <div className="w-20 text-right">
                  <MarginOverrideCell product={p} onSave={(v) => run(() => api.put(`portal-products/${p.id}`, { margin_pct_override: v }), 'Margin updated')} />
                </div>
                <div className="w-24 text-right text-[11px] font-bold text-navy">
                  {!p.priceable
                    ? <span className="text-[9px] font-bold text-amber-700">not orderable</span>
                    : margin != null ? pounds(sell(p.current_cost_pence, margin)) : '—'}
                </div>
                <div className="w-28 text-right flex gap-1 justify-end">
                  <button onClick={() => run(() => api.put(`portal-products/${p.id}`, { status: p.status === 'published' ? 'hidden' : 'published' }), p.status === 'published' ? 'Hidden from the portal' : 'Published')}
                    className="btn-secondary border rounded px-2 py-1 text-[8px] font-bold">
                    {p.status === 'published' ? 'Hide' : 'Publish'}
                  </button>
                </div>
              </div>
            );
          })}
          {!loading && products.length === 0 && (
            <div className="text-center py-8 text-[10px] text-gray-400">Nothing published yet. Use “Publish a product” to put the first item on the portal.</div>
          )}
          <div className="px-3 py-1.5 text-[8px] text-gray-400 bg-gray-50 border-t border-gray-100">
            *Client price at the product’s effective margin before any per-client override. None of the costs or margins on this screen are visible to clients.
          </div>
        </div>
      )}

      {tab === 'publish' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && findCandidates()}
              placeholder="Search the product catalogue by name, code, manufacturer or part number…"
              className="form-input flex-1 text-[11px]" />
            <button onClick={findCandidates} disabled={searching} className="btn-primary rounded px-4 text-[10px] font-bold">
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
          {candidates && candidates.length === 0 && (
            <div className="text-center py-8 text-[10px] text-gray-400 bg-white border border-gray-200 rounded">
              No unpublished catalogue products match. A product must exist in the Product Catalog (with a supplier cost) before it can go on the portal.
            </div>
          )}
          {candidates && candidates.map(c => (
            <PublishCard key={c.id} candidate={c}
              onPublish={async (body) => {
                const ok = await run(() => api.post('portal-products', body), 'Published to the portal');
                if (ok) { setCandidates(null); setSearch(''); setTab('catalogue'); }
              }} />
          ))}
        </div>
      )}

      {tab === 'margins' && (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[8px] uppercase text-gray-500 font-bold">
            <div className="flex-1">Client</div>
            <div className="w-44 text-right">Margin override (blank = default {defaultMargin != null ? `${defaultMargin}%` : ''})</div>
          </div>
          {clients.map(c => (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-50 last:border-0">
              <div className="flex-1 text-[11px] font-semibold text-gray-700">{c.name}</div>
              <div className="w-44 text-right">
                <MarginOverrideCell product={{ margin_pct_override: c.portal_margin_pct }}
                  onSave={(v) => run(() => api.put(`portal-products/client-margins/${c.id}`, { portal_margin_pct: v }), 'Client margin updated')} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MarginOverrideCell({ product, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  if (!editing) {
    return (
      <button onClick={() => { setValue(product.margin_pct_override ?? ''); setEditing(true); }}
        className="text-[10px] text-gray-600 hover:text-navy underline decoration-dotted">
        {product.margin_pct_override != null ? `${product.margin_pct_override}%` : 'default'}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input autoFocus value={value} onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { onSave(value === '' ? null : Number(value)); setEditing(false); }
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder="%" className="form-input w-16 text-[10px] text-right" />
      <button onClick={() => { onSave(value === '' ? null : Number(value)); setEditing(false); }}
        className="text-[9px] font-bold text-navy">Save</button>
    </span>
  );
}

function PublishCard({ candidate, onPublish }) {
  const [supplierId, setSupplierId] = useState(candidate.suppliers.find(s => s.cost_pence != null)?.id || '');
  const [displayName, setDisplayName] = useState(candidate.description || '');
  const [description, setDescription] = useState('');
  const canPublish = supplierId && displayName.trim();
  return (
    <div className="bg-white border border-gray-200 rounded p-3 mb-2">
      <div className="text-[11px] font-bold text-navy">{candidate.description}</div>
      <div className="text-[8px] text-gray-400 mb-2">{candidate.sku} · {candidate.category}{candidate.manufacturer ? ` · ${candidate.manufacturer}` : ''}</div>
      <div className="flex gap-2 flex-wrap mb-2">
        {candidate.suppliers.map(s => (
          <label key={s.id} className={`border rounded px-3 py-1.5 text-[9px] cursor-pointer ${supplierId === s.id ? 'border-navy bg-blue-50 text-navy font-bold' : 'border-gray-200 text-gray-500'}`}>
            <input type="radio" className="hidden" name={`sup-${candidate.id}`} checked={supplierId === s.id} onChange={() => setSupplierId(s.id)} />
            {s.supplier_name}: {pounds(s.cost_pence)}{s.lead_time_days ? ` · ${s.lead_time_days}d lead` : ''}
          </label>
        ))}
        {candidate.suppliers.length === 0 && <span className="text-[9px] text-gray-400">No supplier costs held; add one on the Product Catalog first.</span>}
      </div>
      <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Client-facing name" className="form-input text-[10px]" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Client-facing description (optional)" className="form-input text-[10px]" />
      </div>
      <button disabled={!canPublish}
        onClick={() => onPublish({ product_catalog_id: candidate.id, chosen_supplier_id: supplierId, display_name: displayName.trim(), description: description.trim() || undefined })}
        className={`rounded px-4 py-1.5 text-[10px] font-bold ${canPublish ? 'btn-primary' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
        Publish to the portal
      </button>
    </div>
  );
}
